import React, { FC, memo, useCallback, useContext, useEffect } from 'react'

import { useMutation } from '@apollo/client'
import { useFormContext } from 'react-hook-form'

import { AppContext, CALC_SHIPPING_COST, OTHER } from 'Const'
import { useUserCustomer } from 'Hooks/checkout'
import AddressFields, { FIELDS } from 'Pages/Checkout/AddressFields'
import { isShipping } from 'Utils/cartFulfillment'
import { customerAddressTitle } from 'Utils/user'

interface Props {
  cssPrefix: string
  nameGroupPrefix: string
}

const FulfillmentAddress: FC<Props> = ({ cssPrefix, nameGroupPrefix }) => {
  const { cart, user } = useContext(AppContext)
  const { register, setValue, getValues } = useFormContext()
  const userCustomer = useUserCustomer()
  const [calcShippingCost] = useMutation(CALC_SHIPPING_COST)
  const fulfillment = cart.fulfillment
  const primaryAddress = userCustomer?.customer.primaryAddress
  const customerAddresses = userCustomer?.customer.addresses

  const calcTransportCost = useCallback(() => {
    if (isShipping(fulfillment)) {
      void calcShippingCost({ variables: { address: getValues()['shippingAddress'] } })
    }
  }, [calcShippingCost, fulfillment, getValues])

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target
      if (value === OTHER) {
        FIELDS.map((field) => setValue(`${nameGroupPrefix}[${field}]`, null))
      } else {
        const address = customerAddresses?.find(({ id }) => id === value)
        if (!address) return

        calcTransportCost()
        FIELDS.map((field) => setValue(`${nameGroupPrefix}[${field}]`, address[field]), {
          shouldDirty: true,
        })
      }
    },
    [calcTransportCost, nameGroupPrefix, setValue, customerAddresses],
  )

  useEffect(() => {
    let currentAddress: any
    if (fulfillment?.address) {
      currentAddress = customerAddresses?.find(
        ({ cityGooglePlaceId }) =>
          fulfillment.address?.cityGooglePlaceId && cityGooglePlaceId === fulfillment.address?.cityGooglePlaceId,
      )
      if (!currentAddress) {
        currentAddress = {
          ...fulfillment.address,
          firstName: fulfillment.address.firstName || user?.firstName,
          lastName: fulfillment.address.lastName || user?.lastName,
          id: OTHER,
        }
      }
    } else {
      currentAddress = primaryAddress
    }

    if (currentAddress) {
      setValue('fulfillment.customerAddressId', currentAddress.id)
      FIELDS.map((field) => {
        let value
        if (field === 'address1') {
          value = currentAddress['address1'] || currentAddress['address']
        } else {
          value = currentAddress[field]
        }
        setValue(`${nameGroupPrefix}[${field}]`, value)
      })
    }
  }, [customerAddresses, fulfillment, nameGroupPrefix, primaryAddress, setValue, user])

  return (
    <>
      {userCustomer ? (
        <div className="mb-5">
          <label htmlFor={`${cssPrefix}-customer-address`}>Saved addresses</label>
          <select
            ref={register}
            className="custom-select form-control"
            disabled={!primaryAddress}
            id={`${cssPrefix}-customer-address`}
            name="fulfillment.customerAddressId"
            onChange={handleSelect}
          >
            {customerAddresses?.map((address) => (
              <option key={address.id} value={address.id}>
                {customerAddressTitle(address)}
              </option>
            ))}
            <option value={OTHER}>Other...</option>
          </select>
        </div>
      ) : null}
      <AddressFields calcTransportCost={calcTransportCost} cssPrefix={cssPrefix} nameGroupPrefix={nameGroupPrefix} />
    </>
  )
}

export default memo(FulfillmentAddress)
